/**
 * The referral action — an invite by email. Its own file: the invite is the
 * one action whose payload is a THIRD PARTY's address rather than something
 * the member did, and it carries a different form because of it.
 *
 * The shape follows the flow this replaces: who you're inviting, who it comes
 * from, one full-width action, and the period's allowance said out loud. The
 * cap is the assignment's `maxInvitesPerPeriod`, never a number in here.
 */
import React, {FormEvent, useId, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {boardEarnObjectivesKey} from "@/hooks/ledger-boards/useGetBoardEarnObjectives";
import {useCreateEarnReferral, useGetEarnActionPolicy, earnActionPolicyKey} from "@/hooks/earn/useEarnActions";
import {PolicyStatus, actionErrorMessage, type ActionOutcome} from "./actionFormShell";
import "./earnActionForms.scss";

/** What every action form takes: its objective, the card's label, a refresh callback. */
interface EarnActionFormProps {
    objectiveTemplateId: string;
    label?: string;
    onClaimed?: () => void;
}


export function ReferralActionForm({objectiveTemplateId, onClaimed}: EarnActionFormProps): React.ReactElement {
    const firstId = useId();
    const lastId = useId();
    const emailId = useId();
    const fromId = useId();
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [fromName, setFromName] = useState("");
    const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
    const refer = useCreateEarnReferral();
    const {data: policy, isLoading: policyLoading, isError: policyFailed} = useGetEarnActionPolicy(objectiveTemplateId);
    const queryClient = useQueryClient();

    const cap = policy?.maxClaimsPerPeriod;
    const used = policy?.claimsThisPeriod ?? 0;
    const spent = cap !== undefined && used >= cap;

    const submit = async (e: FormEvent): Promise<void> => {
        e.preventDefault();
        if (!email.trim() || refer.isPending || spent) return;
        setOutcome(null);
        try {
            const result = await refer.mutateAsync({
                objectiveTemplateId,
                recipientEmail: email.trim(),
                recipientFirstName: firstName.trim() || undefined,
                recipientLastName: lastName.trim() || undefined,
                fromName: fromName.trim() || undefined,
            });
            if (result.capReached) {
                setOutcome({tone: "info", text: "You've used all your invites for this period."});
            } else if (result.alreadyClaimed) {
                setOutcome({tone: "info", text: "You've already invited that address."});
            } else {
                setFirstName("");
                setLastName("");
                setEmail("");
                setOutcome({tone: "ok", text: "Invite sent — points land when they join."});
                onClaimed?.();
            }
            void queryClient.invalidateQueries({queryKey: earnActionPolicyKey(objectiveTemplateId)});
            void queryClient.invalidateQueries({queryKey: boardEarnObjectivesKey});
        } catch (err) {
            setOutcome({tone: "error", text: await actionErrorMessage(err)});
        }
    };

    return (
        <form className="earn-action earn-action__stack" onSubmit={submit}>
            <p className="earn-action__lede">
                {policy?.instructions ??
                    (cap
                        ? `Invite up to ${cap} friends per period. Earn points per invitation to join.`
                        : "Invite a friend by email. Points land when they join.")}
            </p>

            {/*
              * THE PLACEHOLDER IS THE LABEL HERE.
              *
              * Each field carried a visible label saying the same words as its
              * own placeholder — "First name" over "Friend's first name" — so
              * the form read twice as long as it is and pushed the send button
              * off a phone screen. The anchor's form has no field labels for
              * exactly this reason; the group heading above already says what
              * the three inputs are.
              *
              * The name moves to `aria-label`, not away: a placeholder is not
              * an accessible name, and it disappears the moment someone types.
              * Same pattern the "Your name" field below already uses.
              */}
            <p className="earn-action__group-label">Who you&apos;re inviting</p>
            <input
                id={firstId}
                className="earn-action__field"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="Friend's first name"
                aria-label="Friend's first name"
                autoComplete="off"
            />
            <input
                id={lastId}
                className="earn-action__field"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder="Friend's last name"
                aria-label="Friend's last name"
                autoComplete="off"
            />
            <input
                id={emailId}
                className="earn-action__field"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Friend's email address"
                aria-label="Friend's email address"
                inputMode="email"
                autoComplete="off"
            />

            <p className="earn-action__group-label">From</p>
            <input
                id={fromId}
                className="earn-action__field"
                value={fromName}
                onChange={e => setFromName(e.target.value)}
                placeholder="Your name"
                aria-label="Your name"
                autoComplete="off"
            />
            <p className="earn-action__hint">Your friend will see the invite as coming from this name.</p>

            <button className="earn-action__action" type="submit" disabled={!email.trim() || refer.isPending || spent}>
                {refer.isPending ? "Sending…" : "Send invite"}
            </button>

            {cap !== undefined &&
                <p className="earn-action__tally">{used} of {cap} referrals this period</p>
            }
            <PolicyStatus isLoading={policyLoading} isError={policyFailed} />
            {outcome && <p className={`earn-action__outcome earn-action__outcome--${outcome.tone}`} role="status">{outcome.text}</p>}
        </form>
    );
}
